import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  IconPlus,
  IconDatabaseOff,
  IconRefresh,
  IconPencil,
  IconCopy,
  IconTrash,
} from '@tabler/icons-react';
import type {
  TicketTypeTemplate,
  TicketTypeTemplateCatalog,
} from '../../../types/ticketTypeTemplate';
import { CreateTicketTypeTemplateDialog } from './CreateTicketTypeTemplateDialog';
import { DuplicateTicketTypeTemplateDialog } from './DuplicateTicketTypeTemplateDialog';
import { TicketTypeTemplateDetailEditor } from './TicketTypeTemplateDetailEditor';
import { EditorModeToggle } from '../EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../../toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../../table';
import type { EditorMode } from '../../../hooks/useJsonGuiToggle';

interface TicketTypeTemplateGuiEditorProps {
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  catalog: TicketTypeTemplateCatalog;
  onCatalogChange: (catalog: TicketTypeTemplateCatalog) => void;
  onSave: () => void;
  onClearCache: () => void;
  onReload: () => void;
  saving: boolean;
  clearing: boolean;
}

export const TicketTypeTemplateGuiEditor: React.FC<TicketTypeTemplateGuiEditorProps> = ({
  mode,
  onModeChange,
  catalog,
  onCatalogChange,
  onSave,
  onClearCache,
  onReload,
  saving,
  clearing,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<TicketTypeTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TicketTypeTemplate | null>(null);
  const [filter, setFilter] = useState('');

  const templates = useMemo(() => catalog.templates ?? [], [catalog.templates]);
  const editing = editingId ? templates.find((t) => t.ticketTypeId === editingId) ?? null : null;

  const filteredTemplates = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((t) =>
      [t.ticketTypeId, t.displayName, t.group]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [templates, filter]);

  const groupSuggestions = useMemo(
    () => Array.from(new Set(templates.map((t) => t.group).filter(Boolean))).sort(),
    [templates],
  );
  const serviceKeySuggestions = useMemo(
    () => Array.from(new Set(templates.map((t) => t.serviceKey).filter(Boolean))).sort(),
    [templates],
  );

  const handleCreate = (template: TicketTypeTemplate) => {
    onCatalogChange({ ...catalog, templates: [...templates, template] });
    setCreateOpen(false);
    setEditingId(template.ticketTypeId);
  };

  const handleTemplateChange = (updated: TicketTypeTemplate) => {
    onCatalogChange({
      ...catalog,
      templates: templates.map((t) =>
        t.ticketTypeId === updated.ticketTypeId ? updated : t,
      ),
    });
  };

  const handleDuplicateConfirm = (newId: string, newName: string) => {
    if (!duplicateSource) return;
    const copy: TicketTypeTemplate = {
      ...duplicateSource,
      ticketTypeId: newId,
      displayName: newName,
      additionalFields: duplicateSource.additionalFields?.map((f) => ({ ...f })) ?? [],
      templateLineItems: duplicateSource.templateLineItems?.map((li) => ({ ...li })) ?? [],
    };
    onCatalogChange({ ...catalog, templates: [...templates, copy] });
    setDuplicateSource(null);
    setEditingId(newId);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    onCatalogChange({
      ...catalog,
      templates: templates.filter((t) => t.ticketTypeId !== deleteTarget.ticketTypeId),
    });
    setDeleteTarget(null);
  };

  // Detail view
  if (editing) {
    return (
      <TicketTypeTemplateDetailEditor
        template={editing}
        followUpTargets={templates
          .filter((t) => t.ticketTypeId && t.ticketTypeId !== editing.ticketTypeId)
          .map((t) => ({ ticketTypeId: t.ticketTypeId, displayName: t.displayName || t.ticketTypeId }))}
        groupSuggestions={groupSuggestions}
        serviceKeySuggestions={serviceKeySuggestions}
        onChange={handleTemplateChange}
        onBack={() => setEditingId(null)}
        onSave={onSave}
        saving={saving}
      />
    );
  }

  // List view
  return (
    <>
      <SecondaryToolbar floating>
        <EditorModeToggle mode={mode} onChange={onModeChange} />
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by ID, name, group…"
        />
        <ToolbarSpacer />
        <ToolbarButton variant="primary" icon={IconPlus} onClick={() => setCreateOpen(true)}>
          Create Template
        </ToolbarButton>
        <ToolbarButton onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
        <ToolbarButton icon={IconDatabaseOff} onClick={onClearCache}>
          {clearing ? 'Clearing…' : 'Clear Cache'}
        </ToolbarButton>
        <ToolbarButton icon={IconRefresh} onClick={onReload}>
          Reload
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Ticket Type ID</DataTableHeadCell>
                <DataTableHeadCell>Display Name</DataTableHeadCell>
                <DataTableHeadCell>Group</DataTableHeadCell>
                <DataTableHeadCell>Billing</DataTableHeadCell>
                <DataTableHeadCell align="center">Fields</DataTableHeadCell>
                <DataTableHeadCell align="center">Items</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.length === 0 ? (
                <DataTableEmpty colSpan={7}>
                  {filter.trim()
                    ? `No templates match "${filter.trim()}".`
                    : 'No templates yet. Click "Create Template" to add one.'}
                </DataTableEmpty>
              ) : (
                filteredTemplates.map((t) => (
                  <DataTableRow key={t.ticketTypeId}>
                    <DataTableCell style={{ fontFamily: 'monospace' }}>
                      {t.ticketTypeId}
                    </DataTableCell>
                    <DataTableCell bold>{t.displayName}</DataTableCell>
                    <DataTableCell>{t.group}</DataTableCell>
                    <DataTableCell muted>
                      {t.billingMode} / {t.billingStrategy}
                    </DataTableCell>
                    <DataTableCell align="center">{t.additionalFields?.length ?? 0}</DataTableCell>
                    <DataTableCell align="center">{t.templateLineItems?.length ?? 0}</DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditingId(t.ticketTypeId)} sx={{ color: '#888' }}>
                          <IconPencil size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => setDuplicateSource(t)} sx={{ color: '#888' }}>
                          <IconCopy size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(t)} sx={{ color: '#888' }}>
                          <IconTrash size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      </Box>

      <CreateTicketTypeTemplateDialog
        open={createOpen}
        existingIds={templates.map((t) => t.ticketTypeId)}
        existingGroups={groupSuggestions}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <DuplicateTicketTypeTemplateDialog
        open={duplicateSource !== null}
        source={duplicateSource}
        existingIds={templates.map((t) => t.ticketTypeId)}
        onClose={() => setDuplicateSource(null)}
        onDuplicate={handleDuplicateConfirm}
      />

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.displayName}"? This cannot be undone
            after saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#888' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disableElevation>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
